import useStyles from "./Case.styles";
import {useDispatch, useSelector} from "react-redux";
import { useCase, useCaseSync, useUndoRedo } from '../../db/hooks';
import Box from "@mui/material/Box";
import {Paper} from "@mui/material";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import {Edit, Check, Undo as UndoIcon, Redo as RedoIcon} from "@mui/icons-material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import React, {useEffect} from "react";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
// Lazy: DemographicsModal pulls in Plotly (multi-MB). Loading it on demand
// keeps the chart library out of the main bundle entirely.
const DemographicsModal = React.lazy(() => import("./DemographicsModal"));
import NotesIcon from "@mui/icons-material/Notes";
import CloudUploadIcon from "@mui/icons-material/Publish";
import ModeSwitch from "./ModeSwitch";
import MultiSelectActionWidget from "../../components/MultiSelect/ActionWidget";
import ScoreCard from "./ScoreCard";
import CaseError from "./CaseError";
import EditModal from "./EditModal";
import UploadCsvModal from "./UploadCsvModal";
import MultiEditModal from "../../components/MultiSelect/MultiEditModal";
import CaseNotes from "./CaseNotes";
import {
    closeCaseModal,
    exportCasePdf
} from "../../state/asyncActions";
import { triggerSync } from '../../db/actions';
import {ActionCreators} from "../../state/actions";
import connectionStatus, {SERVER_REACHABLE} from "../../connectionStatus";
import {ActionCreators as EtherealActionCreators} from "../../state/ethereal/actions";
import {isDemographicsModalOpen} from "../../state/ethereal/selectors";
import OfflineBar from "../../components/OfflineBar";
import JuryBoxAppBar from "../../components/JuryBoxAppBar";
import JurorDialog from "./JurorDialog";
import {Provider} from "../../components/Case/CaseContext";
import { CaseCollaborationProvider } from '../../collaboration/CaseCollaborationContext';
import { useLocation } from 'react-router-dom';

export default ({caseId, topNav, children, isPageLoading = false}) => {
    const classes = useStyles();
    const location = useLocation();

    const [caseNotesOpen, setCaseNotesOpen] = React.useState(false);
    const [caseUploadCsvModalOpen, setCaseUploadCsvModalOpen] = React.useState(false);

    // Use Dexie hooks for case data
    const { caseData, isLoading: caseMetaLoading } = useCase(caseId);
    const { isLoading: syncLoading } = useCaseSync(caseId);
    const caseDataMatches = caseData?._id === caseId;
    const isLoading =
        isPageLoading ||
        caseMetaLoading ||
        (caseData != null && !caseDataMatches);
    
    // Get caseModalOpen from Redux (UI state only)
    const caseModalOpen = useSelector((state) => !!state.app.editingCase);

    const { canUndo, canRedo, undoLabel, redoLabel, undo, redo } = useUndoRedo(caseId);

    const runUndo = React.useCallback(async () => {
        const result = await undo();
        if (result && result.applied === 0) {
            dispatch(EtherealActionCreators.addAlert(
                'Those changes were already modified by someone else.',
                'Nothing to undo',
                'info',
            ));
        }
    }, [undo]);

    const runRedo = React.useCallback(async () => {
        const result = await redo();
        if (result && result.applied === 0) {
            dispatch(EtherealActionCreators.addAlert(
                'Those changes were already modified by someone else.',
                'Nothing to redo',
                'info',
            ));
        }
    }, [redo]);

    // Cmd/Ctrl+Z and Cmd/Ctrl+Shift+Z, except while typing in a field,
    // editor, or open dialog (those have their own undo handling).
    useEffect(() => {
        const onKeyDown = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') {
                return;
            }
            const target = e.target;
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable ||
                target.closest?.('.MuiDialog-root')
            ) {
                return;
            }
            e.preventDefault();
            if (e.shiftKey) {
                runRedo();
            } else {
                runUndo();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [runUndo, runRedo]);

    // Mount the (lazy) demographics modal on first open and keep it mounted
    // after, so the Plotly chunk downloads once and close animations work.
    const demographicsOpen = useSelector(isDemographicsModalOpen);
    const [demographicsRequested, setDemographicsRequested] = React.useState(false);
    useEffect(() => {
        if (demographicsOpen) {
            setDemographicsRequested(true);
        }
    }, [demographicsOpen]);
    
    // Create caseInfo object compatible with existing code
    const emptyCaseInfo = {
        _id: caseId,
        name: '',
        number: '',
        details: '',
        jurors: [],
        spacers: [],
        numberOfAlternates: 0,
        civilCase: false,
        demographicCategories: [],
        showDemographics: false
    };
    const caseInfo = !isLoading && caseData ? caseData : emptyCaseInfo;

    const dispatch = useDispatch();

    const closeEditModal = React.useCallback(async () => {
        dispatch(closeCaseModal(caseId));
    }, [caseId]);

    const openCaseModal = React.useCallback(() => {
        dispatch(ActionCreators.openCaseModal(caseId));
    }, [caseId]);

    useEffect(() => {
        // Initial sync when case page loads
        // Real-time updates are handled by useCaseSync hook (WebSocket-based)
        if (connectionStatus.isServerReachable()) {
            triggerSync();
        }
        
        // Also sync when connection is restored
        const handleConnectionRestored = () => triggerSync();
        connectionStatus.on(SERVER_REACHABLE, handleConnectionRestored);
        
        return () => {
            connectionStatus.removeListener(SERVER_REACHABLE, handleConnectionRestored);
        };
    }, [caseId]);

    // 'idle' | 'generating' | 'done' — drives the download button feedback
    const [pdfStatus, setPdfStatus] = React.useState('idle');

    const downloadPDF = async () => {
        if (pdfStatus === 'generating') {
            return;
        }
        setPdfStatus('generating');
        try {
            await exportCasePdf(caseId, caseInfo.name);
            setPdfStatus('done');
            setTimeout(() => setPdfStatus('idle'), 2000);
        } catch (error) {
            console.error('Failed to export PDF: ', error);
            setPdfStatus('idle');
            dispatch(EtherealActionCreators.addAlert(
                'The PDF could not be generated. Please check your connection and try again.',
                'PDF download failed',
                'error',
            ));
        }
    };

    const openCaseNotes = () => {
        setCaseNotesOpen(true);
    }

    const closeCaseNotes = () => {
        setCaseNotesOpen(false);
    }

    const openCsvModal = () => {
        setCaseUploadCsvModalOpen(true);
    }

    const closeCsvModal = () => {
        setCaseUploadCsvModalOpen(false);
    }

    const showDemographics = React.useCallback(() => {
        dispatch(EtherealActionCreators.setDemographicsModal(true));
    }, [caseId]);

    return (
        <CaseCollaborationProvider caseId={caseId}>
        <Provider value={{
            id: caseId,
            name: caseInfo.name,
            number: caseInfo.number,
            civilCase: caseInfo.civilCase || false,
            numberOfAlternates: caseInfo.numberOfAlternates || 0
        }}>
            <OfflineBar/>
            <JuryBoxAppBar title={isLoading ? (location.state?.caseName || 'Loading case...') : caseInfo.name}/>
            {isLoading && <LinearProgress />}
            <div className={classes.root}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingBottom={1}
                >
                    <Paper elevation={0} className={classes.infoPanel}>
                        <div className={classes.infoPanelTop}>
                            <Typography variant="h5">
                                {caseInfo.name}
                                &nbsp;
                            </Typography>
                            <Tooltip title="Edit case details">
                                <IconButton
                                    onClick={openCaseModal}
                                    className={classes.editButton}
                                    size="small"
                                >
                                    <Edit className={classes.actionButton}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={
                                pdfStatus === 'generating' ? 'Generating PDF…'
                                    : pdfStatus === 'done' ? 'PDF downloaded'
                                    : 'Download PDF of case'
                            }>
                                {/* span keeps the Tooltip working while the button is disabled */}
                                <span>
                                    <IconButton
                                        onClick={downloadPDF}
                                        className={classes.editButton}
                                        size="small"
                                        disabled={pdfStatus === 'generating'}
                                    >
                                        {pdfStatus === 'generating'
                                            ? <CircularProgress size={18} thickness={5}/>
                                            : pdfStatus === 'done'
                                                ? <Check className={classes.actionButton} style={{color: 'green'}}/>
                                                : <CloudDownloadIcon className={classes.actionButton}/>}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {(caseInfo.demographicCategories && caseInfo.demographicCategories.length > 0) &&
                                <React.Fragment>
                                    <Tooltip title="Demographics Analytics">
                                        <IconButton onClick={showDemographics}
                                                    className={classes.demographicsButton}
                                                    size="small">
                                            <SupervisedUserCircleIcon/>
                                        </IconButton>
                                    </Tooltip>

                                    {demographicsRequested &&
                                        <React.Suspense fallback={null}>
                                            <DemographicsModal caseId={caseId}/>
                                        </React.Suspense>
                                    }
                                </React.Fragment>
                            }
                            <Tooltip title="Case Notes">
                                <IconButton onClick={openCaseNotes}
                                            className={classes.demographicsButton}
                                            size="small">
                                    <NotesIcon/>
                                </IconButton>
                            </Tooltip>
                            <div style={{borderLeft: "1px solid #DDD", marginLeft: "5px", paddingLeft: "5px"}}>
                                <Tooltip title={canUndo ? `Undo: ${undoLabel} (⌘Z)` : 'Nothing to undo'}>
                                    <span>
                                        <IconButton onClick={runUndo}
                                                    disabled={!canUndo}
                                                    size="small">
                                            <UndoIcon className={canUndo ? classes.actionButton : undefined}
                                                      style={canUndo ? undefined : {color: 'rgba(0,0,0,0.12)'}}/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={canRedo ? `Redo: ${redoLabel} (⌘⇧Z)` : 'Nothing to redo'}>
                                    <span>
                                        <IconButton onClick={runRedo}
                                                    disabled={!canRedo}
                                                    size="small">
                                            <RedoIcon className={canRedo ? classes.actionButton : undefined}
                                                      style={canRedo ? undefined : {color: 'rgba(0,0,0,0.12)'}}/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </div>
                            <div style={{borderLeft: "1px solid #DDD", marginLeft: "5px", paddingLeft: "5px"}}>
                                <Tooltip title="Upload CSV">
                                    <IconButton
                                        onClick={openCsvModal}
                                        size="small"
                                    >
                                        <CloudUploadIcon className={classes.actionButton}/>
                                    </IconButton>
                                </Tooltip>
                            </div>
                            {!!topNav &&
                                <div style={{borderLeft: "1px solid #DDD", marginLeft: "5px", paddingLeft: "5px"}}>
                                    {topNav}
                                </div>
                            }
                        </div>
                        <Typography
                            className={classes.title}
                            variant="h6"
                            color="textSecondary"
                        >
                            {caseInfo.number}
                        </Typography>
                        <Typography
                            className={classes.title}
                            color="textSecondary"
                        >
                            {caseInfo.details}
                        </Typography>
                    </Paper>
                    <div style={{display: "flex", justifyContent: "flex-end", flex: 1}}>
                        <ModeSwitch caseId={caseId}/>
                        <MultiSelectActionWidget/>
                    </div>
                    {!isLoading && <ScoreCard caseId={caseId}/>}
                </Box>
                <CaseError caseId={caseId}
                           onResolve={openCaseModal}/>
                {children}
                <EditModal
                    key={'edit-modal'}
                    open={caseModalOpen}
                    onClose={closeEditModal}
                    caseData={caseInfo}
                />
                <UploadCsvModal
                    key={'csv-upload-modal'}
                    open={caseUploadCsvModalOpen}
                    onClose={closeCsvModal}
                    caseData={caseInfo}
                />
                <MultiEditModal caseId={caseId}/>
                <CaseNotes caseId={caseId}
                           open={caseNotesOpen}
                           onClose={closeCaseNotes}/>
            </div>
            <JurorDialog
                key={'juror-dialog'}
                caseId={caseId}
            />
        </Provider>
        </CaseCollaborationProvider>
    )
}